import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PointsUpdatePayload } from 'types/interfaces/points';

interface PointsState {
  // Animation queue management
  animationQueue: PointsUpdatePayload[];
  currentAnimation: PointsUpdatePayload | null;
  isAnimating: boolean;

  // Socket connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  reconnectAttempts: number;
}

const initialState: PointsState = {
  animationQueue: [],
  currentAnimation: null,
  isAnimating: false,
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  reconnectAttempts: 0,
};

const pointsSlice = createSlice({
  name: 'points',
  initialState,
  reducers: {
    // Animation actions
    queuePointsChange: (state, action: PayloadAction<PointsUpdatePayload>) => {
      state.animationQueue.push(action.payload);
    },

    startAnimation: (state) => {
      if (state.animationQueue.length > 0 && !state.isAnimating) {
        state.currentAnimation = state.animationQueue[0];
        state.animationQueue = state.animationQueue.slice(1);
        state.isAnimating = true;
      }
    },

    completeAnimation: (state) => {
      state.currentAnimation = null;
      state.isAnimating = false;
    },

    clearAnimationQueue: (state) => {
      state.animationQueue = [];
      state.currentAnimation = null;
      state.isAnimating = false;
    },

    // Socket connection actions
    pointsSocketConnecting: (state) => {
      state.isConnecting = true;
      state.connectionError = null;
    },

    pointsSocketConnected: (state) => {
      state.isConnected = true;
      state.isConnecting = false;
      state.connectionError = null;
      state.reconnectAttempts = 0;
    },

    pointsSocketDisconnected: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
    },

    pointsSocketError: (state, action: PayloadAction<string>) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.connectionError = action.payload;
    },

    pointsSocketReconnectAttempt: (state) => {
      state.reconnectAttempts += 1;
    },

    resetPointsSocketState: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.connectionError = null;
      state.reconnectAttempts = 0;
    },
  },
});

export const {
  queuePointsChange,
  startAnimation,
  completeAnimation,
  clearAnimationQueue,
  pointsSocketConnecting,
  pointsSocketConnected,
  pointsSocketDisconnected,
  pointsSocketError,
  pointsSocketReconnectAttempt,
  resetPointsSocketState,
} = pointsSlice.actions;

// Selectors
export const selectPointsState = (state: { points: PointsState }) => state.points;
export const selectCurrentAnimation = (state: { points: PointsState }) => state.points.currentAnimation;
export const selectIsAnimating = (state: { points: PointsState }) => state.points.isAnimating;
export const selectAnimationQueue = (state: { points: PointsState }) => state.points.animationQueue;
export const selectPointsConnectionStatus = (state: { points: PointsState }) => ({
  isConnected: state.points.isConnected,
  isConnecting: state.points.isConnecting,
  connectionError: state.points.connectionError,
});

export default pointsSlice.reducer;
